import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useStores } from "../../../use-stores";
import { Employee, Resetpassword } from "../../../../../models/Employees/Employee";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { PermissionProject } from "../../../../../models/Employees/PermissionProject";
import EditEmployeePopup from "../../components/employee/EditEmployeePopup";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { EmployeeFormUiStore } from "../../../stores/EmployeeFormUiStore";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";

type EmployeeFormLocationState = {
  listingSearch?: string;
};

const EmployeeFormPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();
  const { employeeStore, userStore } = useStores();
  const isAddMode = location.pathname.endsWith("/employee/add");
  const parsedEmployeeId = Number(employeeId);
  const listingSearch =
    (location.state as EmployeeFormLocationState | null)?.listingSearch || "";
  const uiRef = useRef<EmployeeFormUiStore | null>(null);
  if (!uiRef.current) {
    uiRef.current = new EmployeeFormUiStore();
  }
  const ui = uiRef.current;

  const goToListing = () => {
    employeeStore.removeEmployeeDetails();
    navigate({ pathname: "/employee", search: listingSearch });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        ui.setLoadError(null);
        if (isAddMode) {
          if (!userStore.isSuperAdminManager()) {
            ui.setLoadError("You do not have permission to add an employee.");
            ui.setLoading(false);
            return;
          }
          employeeStore.removeEmployeeDetails();
          if (!cancelled) {
            await employeeStore.loadEligibleTeams("swageazy");
          }
          if (!cancelled) ui.setLoading(false);
          return;
        }

        if (!parsedEmployeeId || Number.isNaN(parsedEmployeeId)) {
          ui.setLoadError("This employee could not be found.");
          ui.setLoading(false);
          return;
        }

        ui.setLoading(true);
        await employeeStore.getEmployeeDetails(parsedEmployeeId, "swageazy");
        if (!cancelled) {
          await employeeStore.loadEligibleTeams("swageazy");
          const teamIds = (employeeStore.employeeDetails?.teams ?? []).map(
            (team) => Number(team.id),
          );
          await employeeStore.loadPermissionsForTeams(teamIds, "swageazy");
        }
      } catch (err: any) {
        if (cancelled) return;
        ui.setLoadError(
          err?.response?.data?.message ||
            "Failed to fetch employee details! Please make sure you have proper rights to fetch other employee details",
        );
      } finally {
        if (!cancelled) ui.setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddMode, parsedEmployeeId]);

  const handleSearchEmployees = useCallback(
    async (search: string): Promise<FilterValue[]> => {
      const employees = await employeeStore.searchEmployees(search);
      const currentId = Number(employeeStore.employeeDetails?.id);
      return employees
        .filter((employee) => Number(employee.id) !== currentId)
        .map((employee) => ({
          id: String(employee.id),
          value: employee.email
            ? `${employee.name} (${employee.email})`
            : String(employee.name || "Employee"),
        }));
    },
    [employeeStore],
  );

  const handleLoadCopySource = useCallback(
    async (employeeId: number) => {
      return employeeStore.loadEmployeeCopySource(
        employeeId,
        employeeStore.permissionProject,
      );
    },
    [employeeStore],
  );

  const handlePermissionProjectChange = async (project: PermissionProject) => {
    const id = employeeStore.employeeDetails?.id;
    try {
      ui.setPermissionsLoading(true);
      ui.setPermissionsError(null);
      if (!isAddMode && id) {
        await employeeStore.getEmployeeDetails(id, project);
      } else {
        employeeStore.setPermissionProject(project);
      }
      await employeeStore.loadEligibleTeams(project);
      const teamIds = (employeeStore.employeeDetails?.teams ?? []).map((team) =>
        Number(team.id),
      );
      await employeeStore.loadPermissionsForTeams(teamIds, project);
    } catch (err: any) {
      ui.setPermissionsError(
        err?.response?.data?.message ||
          "Failed to fetch permissions for the selected project.",
      );
    } finally {
      ui.setPermissionsLoading(false);
    }
  };

  const handleSave = async (employee: Employee) => {
    if (!ui.beginSave()) return;
    const existingId = Number(employee.id || employeeStore.employeeDetails?.id);
    const creating = isAddMode && !existingId;
    ui.setLoadError(null);
    try {
      const response = creating
        ? await employeeStore.addEmployee(employee)
        : await employeeStore.editEmployee(existingId, {
            ...employee,
            id: existingId,
          });
      if (response?.success) {
        const savedId = Number(
          (response.result &&
          typeof response.result === "object" &&
          response.result.id) ||
            existingId,
        );
        if (creating) {
          if (!savedId) {
            ui.setSaveError(
              "Employee was created, but the edit page could not be opened.",
            );
            return;
          }
          navigate(`/employee/${savedId}`, {
            replace: true,
            state: { listingSearch },
          });
          return;
        }
        if (savedId) {
          const project = employeeStore.permissionProject;
          await employeeStore.getEmployeeDetails(savedId, project);
          await employeeStore.loadEligibleTeams(project);
          const teamIds = (employeeStore.employeeDetails?.teams ?? []).map(
            (team) => Number(team.id),
          );
          await employeeStore.loadPermissionsForTeams(teamIds, project);
        }
        ui.bumpCopyResetKey();
        return;
      }
      ui.setSaveError(
        response?.message || "Something went wrong! Please try again later.",
      );
    } catch (err: any) {
      ui.setSaveError(
        err?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    } finally {
      ui.endSave();
    }
  };

  const handleResetPassword = async (email: Resetpassword) => {
    try {
      const response = await employeeStore.resetPassword(email);
      if (response?.success) return;
      throw new Error(
        response?.message || "Something went wrong! Please try again later.",
      );
    } catch (err: any) {
      throw new Error(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong! Please try again later.",
      );
    }
  };

  if (ui.isLoading) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.customColors.greyBackground,
          minHeight: "40vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (ui.loadError || (!isAddMode && !employeeStore.employeeDetails)) {
    return (
      <Grid2 size={{ xs: 12 }} sx={{ px: 2 }}>
        <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <IconButton
            aria-label="Back to employees"
            size="small"
            onClick={goToListing}
            sx={{
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
              borderRadius: 2,
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ mt: 2 }}>
            <InlineFormError
              message={ui.loadError || "This employee could not be found."}
            />
          </Box>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors?.greyTextColor}
            style={{ marginTop: 8 }}
          >
            Return to the employee list and try again.
          </CustomTypography>
        </Paper>
      </Grid2>
    );
  }

  const teamIds =
    employeeStore.employeeDetails?.teams?.map((team) => team.id) || [];
  const canEdit =
    userStore.isSuperAdminManager() || userStore.isTeamManager(teamIds);
  const viewerTeams = userStore.user?.teams ?? [];
  const managedTeamIds = viewerTeams
    .filter((team) => team.is_manager)
    .map((team) => Number(team.id));
  const viewerTeamIds = viewerTeams.map((team) => Number(team.id));
  const employeeTeamIds = new Set(teamIds.map((teamId) => Number(teamId)));
  const visibleTeamIds: number[] | null =
    isAddMode || userStore.isSuperAdminManager()
      ? null
      : userStore.isSuperAdmin() && managedTeamIds.length === 0
        ? null
        : viewerTeamIds.filter((teamId) => employeeTeamIds.has(teamId));

  return (
    <>
      <Backdrop
        open={ui.isSaving}
        sx={{
          color: theme.palette.customColors.white,
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <EditEmployeePopup
        layout="page"
        open
        isAddMode={isAddMode}
        employeeData={employeeStore.employeeDetails}
        saveError={ui.saveError}
        isSaving={ui.isSaving}
        copyResetKey={ui.copyResetKey}
        permissionProject={employeeStore.permissionProject}
        onPermissionProjectChange={handlePermissionProjectChange}
        onClose={goToListing}
        onSave={(employee) => {
          void handleSave(employee);
        }}
        teamsWithPermissions={employeeStore.teamsWithPermissions}
        loadingPermissionTeamIds={employeeStore.loadingPermissionTeamIds}
        onLoadTeamPermissions={async (teamIds) => {
          try {
            ui.setPermissionsError(null);
            await employeeStore.loadPermissionsForTeams(
              teamIds,
              employeeStore.permissionProject,
            );
          } catch (err: any) {
            ui.setPermissionsError(
              err?.response?.data?.message ||
                "Failed to fetch permissions for the selected team.",
            );
            throw err;
          }
        }}
        permissionsLoading={ui.permissionsLoading}
        permissionsError={ui.permissionsError}
        onSearchEmployees={handleSearchEmployees}
        onLoadCopySource={handleLoadCopySource}
        canEditTeam={userStore.isSuperAdminManager()}
        canViewOtherTeams={userStore.hasPermission(EmployeeRoles.ALL)}
        manageableTeamIds={managedTeamIds}
        visibleTeamIds={visibleTeamIds}
        isViewOnly={isAddMode ? false : !canEdit}
        isSuperAdminManager={userStore.isSuperAdminManager()}
        discountPermission={
          !(
            userStore.isSuperAdminManager() ||
            userStore.isTeamManager(teamIds)
          )
        }
        resetPw={
          isAddMode || !userStore.isSuperAdminManager()
            ? undefined
            : (email) => void handleResetPassword(email)
        }
      />
    </>
  );
};

export default observer(EmployeeFormPage);
